/**
 * Feature flags scoped to an Organization. Add new org-scope flags here
 * before using them. Unknown flag names will be rejected at runtime.
 */
export enum OrganizationFeatureFlag {
  SystemEventsCapture = 'systemEventsCapture',
  FormulaJoinEqualityFix = 'formulaJoinEqualityFix',
  NumericFormulaRuntimeErrors = 'numericFormulaRuntimeErrors',
  /**
   * @deprecated Isolated computation is attempted for every seeded formula job, regardless of
   * this value. Retained for compatibility with stored organization flags; hidden from the admin UI.
   */
  IsolatedFormulaComputation = 'isolatedFormulaComputation',
  DisableDependentActionStepValidation = 'disableDependentActionStepValidation',
  EmbedViewType = 'embedViewType',
  EmbedViewerAccess = 'embedViewerAccess',
  WorkflowsV2 = 'workflowsV2',
  ConfigbaseNav = 'configbaseNav',
  OutboundEmailRestate = 'outboundEmailRestate',
  FormulaEvaluatorRestate = 'formulaEvaluatorRestate',
  TableExportRestate = 'tableExportRestate',
  ZTablesDenormalizeCDCRestate = 'zTablesDenormalizeCDCRestate',
  ProcessGmailRestate = 'processGmailRestate',
  /** Managed exclusively by the Restate rollout command because scopes change durable identity. */
  RestateOrganizationScopeV1 = 'restateOrganizationScopeV1',
  RestateOrganizationDirectV1 = 'restateOrganizationDirectV1',
  OrganizationTableExport = 'organizationTableExportEnabled',
  OrganizationExportRestate = 'organizationExportRestate',
  OrganizationHierarchyAdmin = 'organizationHierarchyAdmin',
  /**
   * Keeps this organization's org-tables documents on the LEGACY table-family routing scheme in the
   * v2 Elasticsearch index. Absent (the default for every new organization) means row-family
   * routing in the v3 index. Read only through `getOrgEsRoutingPlan`.
   */
  EsTableFamilyRoutingLegacy = 'esTableFamilyRoutingLegacy',
  /**
   * While set, every write to this organization's org-tables documents is applied to BOTH index
   * versions, each with its own routing key. Set for the duration of a v2 → v3 reindex (and, after
   * the alias flip, for the rollback retention window). Read only through `getOrgEsRoutingPlan`.
   */
  EsRowFamilyRoutingDualWrite = 'esRowFamilyRoutingDualWrite',
}

/**
 * Organization feature flags that describe where an organization's Elasticsearch documents
 * physically live. Toggling either one without also reindexing and moving the org's aliases
 * points every read at an index that may not exist, so the generic admin toggle refuses them;
 * `migrate-elastic-index.ts` writes them directly and remains the only supported path.
 */
export const ES_ROUTING_MANAGED_ORGANIZATION_FEATURE_FLAGS: ReadonlySet<string> = new Set<string>([
  OrganizationFeatureFlag.EsTableFamilyRoutingLegacy,
  OrganizationFeatureFlag.EsRowFamilyRoutingDualWrite,
])

/**
 * Feature flags scoped to an Action. Add new action-scope flags here
 * before using them. Unknown flag names will be rejected at runtime.
 */
export enum ActionFeatureFlag {}

/** Only the rollout command can transition these durable routing flags. */
export const RESTATE_MANAGED_ORGANIZATION_FEATURE_FLAGS: ReadonlySet<string> = new Set<string>([
  OrganizationFeatureFlag.RestateOrganizationScopeV1,
  OrganizationFeatureFlag.RestateOrganizationDirectV1,
])
